import useSWR from "swr";
import {
  rsvpGetResponse,
  RsvpGetResponse,
  RsvpPatchRequest,
} from "../model/rsvp";
import { z } from "zod";
import { errors } from "../errors/errors";

export function useRsvp(authToken: string | null) {
  return useSWR<RsvpGetResponse, SWRError>(
    authToken ? ["/api/rsvp", authToken, rsvpGetResponse] : null,
    fetcher,
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );
}

async function fetcher(
  args: [url: string, authToken: string | null, codec: z.ZodType<any, any, any>]
) {
  const res = await fetch(args[0], {
    method: "GET",
    headers: {
      Authorization: `Bearer ${args[1]}`,
    },
  });

  return handleResponse(res, args[2]);
}

export async function patchRsvp(authToken: string, rsvp: RsvpPatchRequest) {
  const res = await fetch(`/api/rsvp`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "application/json; charset=utf-8",
    },
    body: JSON.stringify(rsvp),
  });

  return handleResponse(res, rsvpGetResponse);
}

async function handleResponse<Codec extends z.ZodType<any, any, any>>(
  res: Response,
  codec: Codec
): Promise<z.infer<Codec>> {
  const responseBodyText = await res.text();
  let responseBodyJSON: Record<string, unknown> | null = null;

  try {
    responseBodyJSON = JSON.parse(responseBodyText);
  } catch (e) {
    throw new SWRError(res.status, errors.schemaError, e);
  }

  if (!res.ok) {
    const maybeApiError = errorData.safeParse(responseBodyJSON);
    throw new SWRError(
      res.status,
      maybeApiError.error ? errors.schemaError : maybeApiError.data.message,
      maybeApiError.error
    );
  } else {
    const decodedResponse = codec.safeParse(responseBodyJSON);
    if (decodedResponse.error) {
      throw new SWRError(1000, errors.schemaError, decodedResponse.error);
    } else {
      return decodedResponse.data;
    }
  }
}

const errorData = z.object({ message: z.string() });

export class SWRError extends Error {
  code: number;
  message: string;
  linkedError?: unknown;

  constructor(code: number, message: string, linkedError?: unknown) {
    super(message);
    this.message = message;
    this.code = code;
    this.linkedError = linkedError;
  }
}
